declare const WHOP_CLI_CHANNEL: string | undefined;
declare const WHOP_CLI_TARGET: string | undefined;
declare const WHOP_CLI_VERSION: string | undefined;

/**
 * Install-channel detection, driven by a build-time constant:
 * tsup defines `WHOP_CLI_CHANNEL="npm"` for the npm package, the standalone
 * binary build defines `"standalone"`, and running from source (tsx) leaves it
 * undefined → `"dev"`. A standalone binary living under a Homebrew prefix is
 * refined to `"brew"` at runtime, since brew installs the same compiled binary
 * but must be upgraded through brew rather than self-update.
 */

export type Channel = "npm" | "standalone" | "brew" | "dev";

const buildChannel: string =
	typeof WHOP_CLI_CHANNEL === "string" ? WHOP_CLI_CHANNEL : "dev";

/** Compile target (e.g. `darwin-arm64`) — only defined in standalone builds. */
export const buildTarget: string | null =
	typeof WHOP_CLI_TARGET === "string" ? WHOP_CLI_TARGET : null;

/**
 * The @whop/cli version this build was published as. Used to pin the
 * `@whop/cli` devDependency in scaffolded projects (they import the vite
 * plugin from `@whop/cli/vite`). Undefined when running from source → callers
 * fall back to `"latest"`.
 */
export const buildVersion: string | null =
	typeof WHOP_CLI_VERSION === "string" ? WHOP_CLI_VERSION : null;

export function isHomebrewPath(
	execPath: string,
	env: NodeJS.ProcessEnv = process.env,
): boolean {
	if (/[/\\](Cellar|homebrew|linuxbrew)[/\\]/.test(execPath)) return true;
	const prefix = env.HOMEBREW_PREFIX?.trim();
	return Boolean(prefix && execPath.startsWith(prefix));
}

export function detectChannel(
	env: NodeJS.ProcessEnv = process.env,
	execPath: string = process.execPath,
): Channel {
	if (buildChannel === "standalone") {
		return isHomebrewPath(execPath, env) ? "brew" : "standalone";
	}
	if (buildChannel === "npm") return "npm";
	return "dev";
}
